import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity } from '../../utils/security';
import { toast } from 'react-toastify';
import {
    Building2, Search, RefreshCw, ChevronRight,
    MessageSquare, Calendar, FileText, Ticket, Bot,
    TrendingUp, CheckCircle2, Clock, AlertCircle, Users,
    Globe, Phone, Mail, Sliders, BarChart3,
    ArrowUpRight, Zap, ShieldCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

// ── 4 CORE STATUS COLOR CATEGORIES ──
// Green: already contracted and working with us
// Yellow: we are working
// Blue: we have offer made but not contracted
// Light Red: we dont have any comunication/problems defined
export const getStatusCategory = (status) => {
    if (['contract_signed', 'supported_live'].includes(status)) {
        return {
            key: 'green',
            label: status === 'supported_live' ? 'ლაივ & მხარდაჭერა' : 'კონტრაქტი გაფორმდა',
            groupTitle: 'კონტრაქტი & პარტნიორი',
            color: 'success',
            borderColor: '#16a34a',
            bgGradient: 'linear-gradient(180deg, #f0fdf4 0%, #ffffff 50%)',
            badgeBg: '#dcfce7',
            badgeText: '#14532d',
            icon: <CheckCircle2 size={13} className="text-success" />
        };
    }
    if (['negotiation', 'meeting_scheduled', 'in_development'].includes(status)) {
        let label = 'მოლაპარაკება';
        if (status === 'meeting_scheduled') label = 'შეხვედრა დაგეგმილია';
        if (status === 'in_development') label = 'დამუშავებაში';
        return {
            key: 'yellow',
            label,
            groupTitle: 'სამუშაო პროცესში',
            color: 'warning',
            borderColor: '#ca8a04',
            bgGradient: 'linear-gradient(180deg, #fefce8 0%, #ffffff 50%)',
            badgeBg: '#fef08a',
            badgeText: '#713f12',
            icon: <Clock size={13} className="text-warning" />
        };
    }
    if (status === 'offer_sent') {
        return {
            key: 'blue',
            label: 'შეთავაზება გაგზავნილია',
            groupTitle: 'შეთავაზება გაკეთებულია',
            color: 'info',
            borderColor: '#2563eb',
            bgGradient: 'linear-gradient(180deg, #eff6ff 0%, #ffffff 50%)',
            badgeBg: '#dbeafe',
            badgeText: '#1e3a8a',
            icon: <Mail size={13} className="text-primary" />
        };
    }
    // Default light red
    return {
        key: 'red',
        label: status === 'rejected' ? 'უარყოფილია' : 'ლიდი / უცნობი',
        groupTitle: 'კომუნიკაციის გარეშე',
        color: 'danger',
        borderColor: '#dc2626',
        bgGradient: 'linear-gradient(180deg, #fef2f2 0%, #ffffff 50%)',
        badgeBg: '#fee2e2',
        badgeText: '#7f1d1d',
        icon: <AlertCircle size={13} className="text-danger" />
    };
};

const OFFER_TYPE_LABELS = {
    create_new: 'ახალი ვებსაიტი',
    redesign_update: 'რედიზაინი',
    maintenance_support: 'მხარდაჭერა',
    cybersecurity_audit: 'კიბერ-აუდიტი',
    all_in_one: 'სრული პაკეტი',
};

const OCCUPIED_SLUGS = ['akhalgori', 'azhara', 'eredvi', 'kurta', 'tighva'];

const AdminMunicipalities = () => {
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    useTranslation();
    const [municipalities, setMunicipalities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all'); // 'all', 'green', 'yellow', 'blue', 'red'
    const [filterRegion, setFilterRegion] = useState('all');
    const [showOccupied, setShowOccupied] = useState(false); // Default hide occupied
    const [showPredictionsModal, setShowPredictionsModal] = useState(false);
    const [simulatedConversion, setSimulatedConversion] = useState(40);

    useEffect(() => {
        if (!authLoading && !isSuperAdmin()) navigate('/admin');
    }, [authLoading, isSuperAdmin, navigate]);

    const fetchMunicipalities = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}municipalities.php?include_occupied=1`);
            const data = await res.json();
            if (Array.isArray(data)) setMunicipalities(data);
        } catch (err) {
            toast.error('მუნიციპალიტეტების ჩატვირთვა ვერ მოხერხდა');
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        if (user) fetchMunicipalities();
    }, [user, fetchMunicipalities]);

    // Split non-occupied and occupied
    const { availableList, occupiedCount } = useMemo(() => {
        const available = [];
        let occupied = 0;
        municipalities.forEach(m => {
            const isOcc = m.is_occupied === 1 || OCCUPIED_SLUGS.includes(m.slug);
            if (isOcc) {
                occupied += 1;
                if (showOccupied) available.push(m);
            } else {
                available.push(m);
            }
        });
        return { availableList: available, occupiedCount: occupied };
    }, [municipalities, showOccupied]);

    const regions = useMemo(() => {
        return [...new Set(availableList.map(m => m.region_ka || m.region).filter(Boolean))].sort();
    }, [availableList]);

    // Core Counts for Top Cards
    const stats = useMemo(() => {
        const nonOccupiedAll = municipalities.filter(m => !(m.is_occupied === 1 || OCCUPIED_SLUGS.includes(m.slug)));
        const green = nonOccupiedAll.filter(m => ['contract_signed', 'supported_live'].includes(m.status));
        const yellow = nonOccupiedAll.filter(m => ['negotiation', 'meeting_scheduled', 'in_development'].includes(m.status));
        const blue = nonOccupiedAll.filter(m => m.status === 'offer_sent');
        const red = nonOccupiedAll.filter(m => ['lead', 'rejected'].includes(m.status));

        const totalRevenue = nonOccupiedAll.reduce((s, m) => s + parseFloat(m.estimated_budget || 0), 0);
        const greenRevenue = green.reduce((s, m) => s + parseFloat(m.estimated_budget || 0), 0);
        const yellowRevenue = yellow.reduce((s, m) => s + parseFloat(m.estimated_budget || 0), 0);
        const blueRevenue = blue.reduce((s, m) => s + parseFloat(m.estimated_budget || 0), 0);
        const redRevenue = red.reduce((s, m) => s + parseFloat(m.estimated_budget || 0), 0);

        return {
            total: nonOccupiedAll.length,
            greenCount: green.length,
            yellowCount: yellow.length,
            blueCount: blue.length,
            redCount: red.length,
            totalRevenue,
            greenRevenue,
            yellowRevenue,
            blueRevenue,
            redRevenue
        };
    }, [municipalities]);

    // Filtered list
    const filtered = useMemo(() => {
        return availableList.filter(m => {
            const cat = getStatusCategory(m.status).key;
            const matchCategory = filterCategory === 'all' || cat === filterCategory;
            const matchSearch = !searchTerm ||
                (m.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (m.name_ka || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (m.region_ka || m.region || '').toLowerCase().includes(searchTerm.toLowerCase());
            const matchRegion = filterRegion === 'all' ||
                (m.region_ka || m.region) === filterRegion;
            return matchCategory && matchSearch && matchRegion;
        });
    }, [availableList, filterCategory, searchTerm, filterRegion]);

    // Simulated Projected Revenue in Modal
    const simulatedProjection = useMemo(() => {
        const base = (stats.greenRevenue * 1.0) + (stats.yellowRevenue * 0.7) + (stats.blueRevenue * (simulatedConversion / 100)) + (stats.redRevenue * 0.15);
        return Math.round(base);
    }, [stats, simulatedConversion]);

    if (authLoading || !user) return null;

    return (
        <AdminLayout title="მუნიციპალიტეტების მართვა">
            {/* ── TOP INTERACTIVE STATS CARDS (CLICK TO FILTER) ── */}
            <div className="row g-3 mb-4">
                {/* 1. All Municipalities Card */}
                <div className="col-6 col-md-4 col-xl-2">
                    <div 
                        className="shadcn-card p-3 h-100 cursor-pointer text-start position-relative"
                        onClick={() => setFilterCategory('all')}
                        style={{ 
                            cursor: 'pointer',
                            border: filterCategory === 'all' ? '2px solid #6366f1' : '1px solid #cbd5e1',
                            background: filterCategory === 'all' ? '#eef2ff' : '#ffffff',
                            transition: 'all 0.2s ease',
                            transform: filterCategory === 'all' ? 'scale(1.02)' : 'none'
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="text-primary"><Building2 size={20} /></span>
                            {filterCategory === 'all' && (
                                <span className="badge bg-primary text-white rounded-pill smallest">აქტიური</span>
                            )}
                        </div>
                        <div className="fw-bold fs-4" style={{ color: '#000000' }}>{stats.total}</div>
                        <div className="smallest fw-bold" style={{ color: '#334155' }}>სულ (არაოკუპირებული)</div>
                    </div>
                </div>

                {/* 2. GREEN: Contracted & Live */}
                <div className="col-6 col-md-4 col-xl-2">
                    <div 
                        className="shadcn-card p-3 h-100 cursor-pointer text-start position-relative"
                        onClick={() => setFilterCategory(filterCategory === 'green' ? 'all' : 'green')}
                        style={{ 
                            cursor: 'pointer',
                            border: filterCategory === 'green' ? '2px solid #16a34a' : '1.5px solid #86efac',
                            background: filterCategory === 'green' ? '#f0fdf4' : '#ffffff',
                            transition: 'all 0.2s ease',
                            transform: filterCategory === 'green' ? 'scale(1.02)' : 'none'
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="text-success"><CheckCircle2 size={20} /></span>
                            <span className="badge rounded-pill smallest fw-bold" style={{ background: '#dcfce7', color: '#14532d' }}>🟢</span>
                        </div>
                        <div className="fw-bold fs-4" style={{ color: '#15803d' }}>{stats.greenCount}</div>
                        <div className="smallest fw-bold" style={{ color: '#14532d' }}>კონტრაქტი / ლაივ</div>
                    </div>
                </div>

                {/* 3. YELLOW: Working / In Progress */}
                <div className="col-6 col-md-4 col-xl-2">
                    <div 
                        className="shadcn-card p-3 h-100 cursor-pointer text-start position-relative"
                        onClick={() => setFilterCategory(filterCategory === 'yellow' ? 'all' : 'yellow')}
                        style={{ 
                            cursor: 'pointer',
                            border: filterCategory === 'yellow' ? '2px solid #ca8a04' : '1.5px solid #fde047',
                            background: filterCategory === 'yellow' ? '#fefce8' : '#ffffff',
                            transition: 'all 0.2s ease',
                            transform: filterCategory === 'yellow' ? 'scale(1.02)' : 'none'
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <span style={{ color: '#ca8a04' }}><Clock size={20} /></span>
                            <span className="badge rounded-pill smallest fw-bold" style={{ background: '#fef08a', color: '#713f12' }}>🟡</span>
                        </div>
                        <div className="fw-bold fs-4" style={{ color: '#a16207' }}>{stats.yellowCount}</div>
                        <div className="smallest fw-bold" style={{ color: '#713f12' }}>სამუშაო პროცესში</div>
                    </div>
                </div>

                {/* 4. BLUE: Offer Sent */}
                <div className="col-6 col-md-4 col-xl-2">
                    <div 
                        className="shadcn-card p-3 h-100 cursor-pointer text-start position-relative"
                        onClick={() => setFilterCategory(filterCategory === 'blue' ? 'all' : 'blue')}
                        style={{ 
                            cursor: 'pointer',
                            border: filterCategory === 'blue' ? '2px solid #2563eb' : '1.5px solid #93c5fd',
                            background: filterCategory === 'blue' ? '#eff6ff' : '#ffffff',
                            transition: 'all 0.2s ease',
                            transform: filterCategory === 'blue' ? 'scale(1.02)' : 'none'
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="text-primary"><Mail size={20} /></span>
                            <span className="badge rounded-pill smallest fw-bold" style={{ background: '#dbeafe', color: '#1e3a8a' }}>🔵</span>
                        </div>
                        <div className="fw-bold fs-4" style={{ color: '#1d4ed8' }}>{stats.blueCount}</div>
                        <div className="smallest fw-bold" style={{ color: '#1e3a8a' }}>შეთავაზება გაგზავნილი</div>
                    </div>
                </div>

                {/* 5. LIGHT RED: Leads / No Communication */}
                <div className="col-6 col-md-4 col-xl-2">
                    <div 
                        className="shadcn-card p-3 h-100 cursor-pointer text-start position-relative"
                        onClick={() => setFilterCategory(filterCategory === 'red' ? 'all' : 'red')}
                        style={{ 
                            cursor: 'pointer',
                            border: filterCategory === 'red' ? '2px solid #dc2626' : '1.5px solid #fca5a5',
                            background: filterCategory === 'red' ? '#fef2f2' : '#ffffff',
                            transition: 'all 0.2s ease',
                            transform: filterCategory === 'red' ? 'scale(1.02)' : 'none'
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="text-danger"><AlertCircle size={20} /></span>
                            <span className="badge rounded-pill smallest fw-bold" style={{ background: '#fee2e2', color: '#7f1d1d' }}>🔴</span>
                        </div>
                        <div className="fw-bold fs-4" style={{ color: '#b91c1c' }}>{stats.redCount}</div>
                        <div className="smallest fw-bold" style={{ color: '#7f1d1d' }}>კომუნიკაციის გარეშე</div>
                    </div>
                </div>

                {/* 6. LAST STATS CARD: LINK & MODAL TO DETAILED CHARTS & PREDICTIONS */}
                <div className="col-12 col-md-4 col-xl-2">
                    <div 
                        className="shadcn-card p-3 h-100 cursor-pointer text-start position-relative"
                        onClick={() => setShowPredictionsModal(true)}
                        style={{ 
                            cursor: 'pointer',
                            border: '2px solid #8b5cf6',
                            background: '#faf5ff',
                            transition: 'all 0.25s ease'
                        }}
                        title="დააჭირეთ პროგნოზებისა და ანალიტიკის სანახავად"
                    >
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <span style={{ color: '#7c3aed' }}><TrendingUp size={20} /></span>
                            <span className="badge border shadow-sm rounded-pill smallest d-flex align-items-center gap-1 fw-bold" style={{ background: '#ffffff', color: '#6d28d9', borderColor: '#c4b5fd' }}>
                                AI პროგნოზი <ArrowUpRight size={10} />
                            </span>
                        </div>
                        <div className="fw-bold fs-4" style={{ color: '#5b21b6' }}>₾{(stats.totalRevenue / 1000).toFixed(0)}k</div>
                        <div className="smallest fw-bold d-flex align-items-center justify-content-between" style={{ color: '#4c1d95' }}>
                            <span>სავარაუდო შემოსავ.</span>
                            <span style={{ color: '#7c3aed' }} className="fw-bold">დეტალები &raquo;</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── AI AUTONOMOUS NOTICE ── */}
            <div className="p-3 rounded-3 mb-4 d-flex align-items-center justify-content-between gap-3 flex-wrap"
                style={{ background: '#f5f3ff', border: '1px solid #ddd6fe' }}>
                <div className="d-flex align-items-center gap-3">
                    <div className="rounded-circle p-2" style={{ background: '#6366f1', flexShrink: 0 }}>
                        <Bot size={20} className="text-white" />
                    </div>
                    <div>
                        <div className="fw-bold small" style={{ color: '#000000' }}>🤖 ავტონომიური ვებ-სააგენტოს CRM მართვის პანელი</div>
                        <div className="smallest fw-medium" style={{ color: '#475569' }}>
                            დააწკაპუნეთ ზედა ბარათებზე სტატუსით გასაფილტრად (🟢 კონტრაქტი, 🟡 პროცესში, 🔵 შეთავაზება, 🔴 ლიდები).
                        </div>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <button 
                        className="shadcn-btn shadcn-btn-primary shadcn-btn-sm fw-semibold"
                        onClick={() => navigate('/admin/potential-customers')}
                    >
                        <Users size={14} /> პოტენციური კლიენტები (B2B Leads)
                    </button>
                    <button 
                        className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm fw-semibold"
                        style={{ color: '#000000', borderColor: '#cbd5e1' }}
                        onClick={() => navigate('/admin/municipalities/analytics')}
                    >
                        <BarChart3 size={14} /> სრული ანალიტიკა & პროგნოზი
                    </button>
                    <span className="shadcn-badge shadcn-badge-success d-flex align-items-center gap-1 fw-bold" style={{ color: '#14532d', background: '#dcfce7' }}>
                        <span className="rounded-circle" style={{ width: '6px', height: '6px', display: 'inline-block', background: '#16a34a' }}></span>
                        64 მუნიციპალიტეტი აქტიური
                    </span>
                </div>
            </div>

            {/* ── FILTERS & SEARCH BAR ── */}
            <div className="shadcn-card mb-4">
                <div className="d-flex flex-column flex-lg-row gap-3 align-items-lg-center justify-content-between">
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                        <div className="header-search-bar" style={{ width: '260px' }}>
                            <Search size={16} className="text-muted" />
                            <input
                                type="text"
                                placeholder="ძებნა სახელით, რეგიონით..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {/* Status Category Dropdown Filter */}
                        <select
                            className="shadcn-input"
                            style={{ width: '220px' }}
                            value={filterCategory}
                            onChange={e => setFilterCategory(e.target.value)}
                        >
                            <option value="all">ყველა სტატუსი ({stats.total})</option>
                            <option value="green">🟢 კონტრაქტი & ლაივ ({stats.greenCount})</option>
                            <option value="yellow">🟡 სამუშაო პროცესში ({stats.yellowCount})</option>
                            <option value="blue">🔵 შეთავაზება გაგზავნილია ({stats.blueCount})</option>
                            <option value="red">🔴 კომუნიკაციის გარეშე ({stats.redCount})</option>
                        </select>

                        {/* Region Dropdown Filter */}
                        <select
                            className="shadcn-input"
                            style={{ width: '200px' }}
                            value={filterRegion}
                            onChange={e => setFilterRegion(e.target.value)}
                        >
                            <option value="all">ყველა რეგიონი</option>
                            {regions.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>

                        {/* Optional Occupied Toggle */}
                        <label className="d-flex align-items-center gap-1 text-muted smallest cursor-pointer ms-2 mb-0" style={{ cursor: 'pointer' }}>
                            <input 
                                type="checkbox" 
                                checked={showOccupied} 
                                onChange={e => setShowOccupied(e.target.checked)} 
                                className="form-check-input mt-0"
                            />
                            <span>ოკუპირებული მუნიციპალიტეტების გამოჩენა ({occupiedCount})</span>
                        </label>
                    </div>

                    <div className="d-flex gap-2 ms-lg-auto align-items-center">
                        <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={fetchMunicipalities}>
                            <RefreshCw size={14} /> განახლება
                        </button>
                        <span className="shadcn-badge shadcn-badge-info d-flex align-items-center">
                            {filtered.length} / {availableList.length}
                        </span>
                    </div>
                </div>
            </div>

            {/* ── MUNICIPALITIES GRID WITH COLOR-CODED BORDERS & BACKGROUNDS ── */}
            {loading ? (
                <div className="text-center py-5 text-muted">
                    <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                    <span>მუნიციპალიტეტები იტვირთება...</span>
                </div>
            ) : (
                <div className="row g-3">
                    {filtered.map(m => {
                        const statusCat = getStatusCategory(m.status);
                        const isOcc = m.is_occupied === 1 || OCCUPIED_SLUGS.includes(m.slug);
                        const comms = parseInt(m.comm_count || 0);
                        const files = parseInt(m.files_count || 0);
                        const events = parseInt(m.upcoming_events_count || 0);
                        const tickets = parseInt(m.tickets_count || 0);

                        return (
                            <div key={m.id} className="col-12 col-md-6 col-xl-4">
                                <div
                                    className="shadcn-card h-100 cursor-pointer municipality-card position-relative overflow-hidden"
                                    onClick={() => navigate(`/admin/municipalities/${m.id}`)}
                                    style={{
                                        cursor: 'pointer',
                                        transition: 'all 0.25s ease',
                                        border: `2px solid ${statusCat.borderColor}`,
                                        background: statusCat.bgGradient,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
                                    }}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.transform = 'translateY(-3px)';
                                        e.currentTarget.style.boxShadow = `0 10px 25px ${statusCat.borderColor}25`;
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.03)';
                                    }}
                                >
                                    {/* Occupied Badge if displayed */}
                                    {isOcc && (
                                        <div className="position-absolute top-0 end-0 bg-danger text-white smallest px-2 py-0 rounded-bottom-start fw-bold">
                                            ოკუპირებული
                                        </div>
                                    )}

                                    {/* Card Header with Logo */}
                                    <div className="d-flex align-items-start gap-3 mb-3">
                                        <div style={{
                                            width: '54px', height: '54px', borderRadius: '10px', overflow: 'hidden',
                                            border: `2px solid ${statusCat.borderColor}50`, flexShrink: 0, background: '#ffffff',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                                        }}>
                                            {m.logo ? (
                                                <img
                                                    src={m.logo}
                                                    alt={m.name}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    onError={e => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div style="font-size:22px">🏛️</div>'; }}
                                                />
                                            ) : (
                                                <span style={{ fontSize: '22px' }}>🏛️</span>
                                            )}
                                        </div>
                                        <div className="flex-grow-1 min-w-0">
                                            <div className="d-flex align-items-center justify-content-between gap-2">
                                                <h6 className="fw-bold mb-0 text-truncate municipality-title" style={{ maxWidth: '170px', color: '#000000', fontSize: '15px' }}>
                                                    {m.name_ka || m.name}
                                                </h6>
                                                {m.is_city === 1 && (
                                                    <span className="badge bg-primary rounded-pill smallest" style={{ fontSize: '10px', color: '#ffffff' }}>ქ.</span>
                                                )}
                                            </div>
                                            <div className="smallest text-truncate municipality-region fw-semibold" style={{ color: '#475569', marginTop: '2px' }}>
                                                {m.region_ka || m.region}
                                            </div>
                                            
                                            {/* Status Badge with Color */}
                                            <div className="mt-1">
                                                <span 
                                                    className="badge d-inline-flex align-items-center gap-1 rounded-pill fw-bold"
                                                    style={{ 
                                                        background: statusCat.badgeBg, 
                                                        color: statusCat.badgeText,
                                                        border: `1px solid ${statusCat.borderColor}60`,
                                                        fontSize: '11px',
                                                        padding: '3px 8px'
                                                    }}
                                                >
                                                    {statusCat.icon}
                                                    <span style={{ color: statusCat.badgeText }}>{statusCat.label}</span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Description / Notes if available */}
                                    {m.notes && (
                                        <div className="municipality-desc small mb-3 p-2 rounded-2" style={{ background: '#ffffff', color: '#1e293b', border: '1px solid #cbd5e1', fontSize: '12px', lineHeight: 1.5, maxHeight: '60px', overflow: 'hidden' }}>
                                            <strong style={{ color: '#000000' }}>აღწერა: </strong>
                                            <span style={{ color: '#334155' }}>{m.notes}</span>
                                        </div>
                                    )}

                                    {/* Offer Type & Budget */}
                                    <div className="d-flex justify-content-between align-items-center p-2 rounded-3 mb-3 offer-box" style={{ background: '#ffffff', border: '1px solid #cbd5e1' }}>
                                        <div>
                                            <div className="smallest fw-semibold" style={{ color: '#475569' }}>შეთავაზება</div>
                                            <div className="small fw-bold" style={{ color: '#000000' }}>{OFFER_TYPE_LABELS[m.offer_type] || m.offer_type}</div>
                                        </div>
                                        <div className="text-end">
                                            <div className="smallest fw-semibold" style={{ color: '#475569' }}>ბიუჯეტი</div>
                                            <div className="small fw-bold" style={{ color: '#15803d' }}>₾{parseFloat(m.estimated_budget || 0).toLocaleString()}</div>
                                        </div>
                                    </div>

                                    {/* Activity Counters */}
                                    <div className="d-flex gap-2 mb-3 activity-box py-1 px-2 rounded-2" style={{ background: '#ffffff', border: '1px solid #cbd5e1' }}>
                                        {[
                                            { count: comms, icon: <MessageSquare size={13} />, label: 'ჩატი', color: '#2563eb' },
                                            { count: events, icon: <Calendar size={13} />, label: 'ღონ.', color: '#d97706' },
                                            { count: files, icon: <FileText size={13} />, label: 'ფაილი', color: '#0891b2' },
                                            { count: tickets, icon: <Ticket size={13} />, label: 'ტიკეტი', color: '#dc2626' },
                                        ].map((item, i) => (
                                            <div key={i} className="d-flex flex-column align-items-center flex-fill">
                                                <div className="d-flex align-items-center gap-1" style={{ color: item.color }}>{item.icon}</div>
                                                <div className="fw-bold small" style={{ color: '#000000' }}>{item.count}</div>
                                                <div className="fw-semibold" style={{ fontSize: '10px', color: '#475569' }}>{item.label}</div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Contact & Links */}
                                    <div className="d-flex align-items-center justify-content-between border-top pt-2 mt-auto" style={{ borderColor: '#e2e8f0' }}>
                                        <div className="d-flex gap-2">
                                            {m.official_website && (
                                                <a
                                                    href={m.official_website}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-outline-secondary p-1 lh-1"
                                                    style={{ borderColor: '#cbd5e1', color: '#1e293b', background: '#ffffff' }}
                                                    title="ოფიციალური ვებსაიტი"
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <Globe size={13} />
                                                </a>
                                            )}
                                            {m.contact_email && (
                                                <a
                                                    href={`mailto:${m.contact_email}`}
                                                    className="btn btn-sm btn-outline-secondary p-1 lh-1"
                                                    style={{ borderColor: '#cbd5e1', color: '#1e293b', background: '#ffffff' }}
                                                    title={m.contact_email}
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <Mail size={13} />
                                                </a>
                                            )}
                                            {m.contact_phone && (
                                                <a
                                                    href={`tel:${m.contact_phone}`}
                                                    className="btn btn-sm btn-outline-secondary p-1 lh-1"
                                                    style={{ borderColor: '#cbd5e1', color: '#1e293b', background: '#ffffff' }}
                                                    title={m.contact_phone}
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <Phone size={13} />
                                                </a>
                                            )}
                                        </div>
                                        <button
                                            className="shadcn-btn shadcn-btn-primary shadcn-btn-sm d-flex align-items-center gap-1 fw-semibold"
                                            style={{ color: '#ffffff !important' }}
                                            onClick={e => { e.stopPropagation(); navigate(`/admin/municipalities/${m.id}`); }}
                                        >
                                            დეტალები <ChevronRight size={14} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {filtered.length === 0 && (
                        <div className="col-12">
                            <div className="text-center py-5 text-muted">
                                <Building2 size={48} className="mb-3 opacity-25" />
                                <p className="mb-2">მუნიციპალიტეტები ამ ფილტრით არ მოიძებნა</p>
                                <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={() => { setFilterCategory('all'); setSearchTerm(''); setFilterRegion('all'); }}>
                                    ფილტრების გასუფთავება
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ── FULLSCREEN PREDICTIONS & DETAILED CHARTS MODAL POPUP ── */}
            {showPredictionsModal && (
                <div 
                    className="modal fade show d-block" 
                    tabIndex="-1" 
                    style={{ backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(8px)', zIndex: 1050 }}
                    onClick={() => setShowPredictionsModal(false)}
                >
                    <div 
                        className="modal-dialog modal-dialog-centered modal-xl" 
                        onClick={e => e.stopPropagation()}
                        style={{ maxWidth: '1150px' }}
                    >
                        <div className="modal-content shadow-2xl border-0 rounded-4 overflow-hidden">
                            {/* Modal Header */}
                            <div className="modal-header bg-slate-900 text-white p-4 d-flex justify-content-between align-items-center" style={{ background: '#0f172a' }}>
                                <div className="d-flex align-items-center gap-3">
                                    <div className="p-2 rounded bg-indigo text-white" style={{ background: '#6366f1' }}>
                                        <TrendingUp size={22} />
                                    </div>
                                    <div>
                                        <h5 className="modal-title fw-bold text-white mb-0">
                                            მუნიციპალური შემოსავლების AI პროგნოზი & ბაზრის ანალიტიკა
                                        </h5>
                                        <span className="smallest text-slate-400" style={{ color: '#94a3b8' }}>
                                            საქართველოს 64 მუნიციპალიტეტის კონვერტაციის მოდელირება და ARR პროგნოზები
                                        </span>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <button 
                                        className="btn btn-sm btn-outline-light rounded-pill px-3 d-flex align-items-center gap-1"
                                        onClick={() => {
                                            setShowPredictionsModal(false);
                                            navigate('/admin/municipalities/analytics');
                                        }}
                                    >
                                        <span>სრულ გვერდზე გადასვლა</span>
                                        <ArrowUpRight size={14} />
                                    </button>
                                    <button 
                                        type="button" 
                                        className="btn-close btn-close-white" 
                                        onClick={() => setShowPredictionsModal(false)} 
                                    />
                                </div>
                            </div>

                            {/* Modal Body */}
                            <div className="modal-body p-4 bg-light overflow-auto" style={{ maxHeight: '80vh' }}>
                                {/* 4 Status KPI Summary */}
                                <div className="row g-3 mb-4">
                                    <div className="col-6 col-md-3">
                                        <div className="p-3 rounded-3 bg-white border border-success border-opacity-40">
                                            <div className="smallest text-muted fw-bold">🟢 კონტრაქტი & ლაივ</div>
                                            <div className="fs-4 fw-bold text-success">{stats.greenCount}</div>
                                            <div className="smallest text-muted">₾{stats.greenRevenue.toLocaleString()} ბიუჯეტი</div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="p-3 rounded-3 bg-white border border-warning border-opacity-40">
                                            <div className="smallest text-muted fw-bold">🟡 სამუშაო პროცესში</div>
                                            <div className="fs-4 fw-bold text-warning">{stats.yellowCount}</div>
                                            <div className="smallest text-muted">₾{stats.yellowRevenue.toLocaleString()} ბიუჯეტი</div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="p-3 rounded-3 bg-white border border-primary border-opacity-40">
                                            <div className="smallest text-muted fw-bold">🔵 შეთავაზება გაგზავნილი</div>
                                            <div className="fs-4 fw-bold text-primary">{stats.blueCount}</div>
                                            <div className="smallest text-muted">₾{stats.blueRevenue.toLocaleString()} ბიუჯეტი</div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="p-3 rounded-3 bg-white border border-danger border-opacity-40">
                                            <div className="smallest text-muted fw-bold">🔴 კომუნიკაციის გარეშე</div>
                                            <div className="fs-4 fw-bold text-danger">{stats.redCount}</div>
                                            <div className="smallest text-muted">₾{stats.redRevenue.toLocaleString()} ბიუჯეტი</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Interactive Simulator Slider */}
                                <div className="p-4 bg-white rounded-3 border mb-4 shadow-sm">
                                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <Sliders size={18} className="text-primary" />
                                            <span className="fw-bold text-dark">შეთავაზებების კონვერტაციის სიმულაცია:</span>
                                            <span className="badge bg-primary fs-7">{simulatedConversion}% ალბათობა</span>
                                        </div>
                                        <div className="fw-bold fs-5 text-success">
                                            პროგნოზირებული შემოსავალი: ₾{simulatedProjection.toLocaleString()}
                                        </div>
                                    </div>
                                    <input 
                                        type="range" 
                                        min="10" 
                                        max="90" 
                                        step="5"
                                        value={simulatedConversion} 
                                        onChange={e => setSimulatedConversion(parseInt(e.target.value))}
                                        className="form-range"
                                    />
                                    <div className="d-flex justify-content-between text-muted smallest">
                                        <span>10% (პესიმისტური)</span>
                                        <span>40% (რეალისტური)</span>
                                        <span>90% (ოპტიმისტური)</span>
                                    </div>
                                </div>

                                {/* Autonomous Agency Projections Grid */}
                                <div className="row g-3">
                                    <div className="col-12 col-md-6">
                                        <div className="p-3 bg-white rounded-3 border h-100">
                                            <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                                                <Zap size={16} className="text-warning" />
                                                წლიური ხელშეკრულებების პოტენციალი (ARR)
                                            </h6>
                                            <div className="d-flex flex-column gap-2 small">
                                                <div className="d-flex justify-content-between p-2 rounded bg-light">
                                                    <span>ვებსაიტების რედიზაინი (საშუალო ₾22,000)</span>
                                                    <span className="fw-bold text-dark">₾1,400,000+</span>
                                                </div>
                                                <div className="d-flex justify-content-between p-2 rounded bg-light">
                                                    <span>კიბერ-უსაფრთხოების აუდიტი & მონიტორინგი</span>
                                                    <span className="fw-bold text-dark">₾350,000+</span>
                                                </div>
                                                <div className="d-flex justify-content-between p-2 rounded bg-light">
                                                    <span>ყოველთვიური ტექნიკური მხარდაჭერა (24/7)</span>
                                                    <span className="fw-bold text-success">₾1,920,000 / წელი</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6">
                                        <div className="p-3 bg-white rounded-3 border h-100">
                                            <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                                                <ShieldCheck size={16} className="text-success" />
                                                ავტონომიური სისტემის წარმადობა
                                            </h6>
                                            <div className="d-flex flex-column gap-2 small">
                                                <div className="d-flex justify-content-between p-2 rounded bg-light">
                                                    <span>AI შეთავაზების გენერირების სიჩქარე:</span>
                                                    <span className="fw-bold text-primary">&lt; 3 წამი</span>
                                                </div>
                                                <div className="d-flex justify-content-between p-2 rounded bg-light">
                                                    <span>კალენდარული შეხვედრების ინტეგრაცია:</span>
                                                    <span className="fw-bold text-success">Google Meet & Zoom</span>
                                                </div>
                                                <div className="d-flex justify-content-between p-2 rounded bg-light">
                                                    <span>მუნიციპალური ტიკეტების ცენტრალიზება:</span>
                                                    <span className="fw-bold text-indigo">100% აქტიური</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="modal-footer bg-white p-3 d-flex justify-content-between flex-wrap gap-2">
                                <span className="text-muted smallest">
                                    მონაცემები გენერირებულია OrbitroniX Autonomous CRM Engine-ის მიერ
                                </span>
                                <div className="d-flex gap-2 flex-wrap">
                                    <button 
                                        className="shadcn-btn shadcn-btn-secondary" 
                                        onClick={() => setShowPredictionsModal(false)}
                                    >
                                        დახურვა
                                    </button>
                                    <button 
                                        className="shadcn-btn shadcn-btn-secondary" 
                                        onClick={() => {
                                            setShowPredictionsModal(false);
                                            navigate('/admin/municipalities/analytics');
                                        }}
                                    >
                                        📊 AI ბაზრის ანალიტიკა
                                    </button>
                                    <button 
                                        className="shadcn-btn shadcn-btn-primary" 
                                        onClick={() => {
                                            setShowPredictionsModal(false);
                                            navigate('/admin/municipalities/report');
                                        }}
                                    >
                                        📄 სრული ანგარიში &amp; კალკულატორი &raquo;
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default AdminMunicipalities;
